// #1167/#1168 RU carrier card — pure presentational.
//
// Surfaces:
//   - displayCode chip (`R-N`, per-request)
//   - PII-gated name + contacts (omitted entirely when the wire strips
//     them — i.e. the actor lacks `russiaCarriers.read`)
//   - state pill + reject reason
//   - bid list (price + days-left badge + comment); `expiresAt` /
//     `isExpired` are server-computed
//   - "Add bid" CTA when canEdit and the carrier is not REJECTED
//   - kebab menus (#1168) on the card (Edit/Delete carrier) and on each
//     bid row (Edit/Delete bid), gated by `canEdit`
//
// @aiPurpose presentational only; the parent owns modal arbitration.
// @aiSecurity name/contacts may be absent — the card MUST NOT
//   construct any fallback that leaks identity (no "Carrier #<id>",
//   no contacts heading without contacts).

import { useTranslation } from 'react-i18next';
import type { RussiaRequestCarrier, RussiaRequestCarrierBid } from '@magimp/shared';
import { Button, IconButton, Menu, MenuItem } from '../../../../ui/index.js';
import { CarrierStatePill } from '../../../requests/display/index.js';

export interface RussiaCarrierBidCardProps {
  carrier: RussiaRequestCarrier;
  canEdit: boolean;
  onAddBid: () => void;
  /** #1168 — kebab menu callbacks. Absent ⇒ kebab not rendered. */
  onEditCarrier?: () => void;
  onDeleteCarrier?: () => void;
  onEditBid?: (bid: RussiaRequestCarrierBid) => void;
  onDeleteBid?: (bid: RussiaRequestCarrierBid) => void;
  /**
   * #1544 — manager-side winner pick. `canPickWinner` gates the toggle
   * (authority × status decided by the parent); `winnerBidId` marks the
   * currently-stored winner; `onPickWinner` fires the pick mutation. The
   * control is PII-safe: it only toggles the cost-bearing bid, never
   * surfaces carrier identity.
   */
  canPickWinner?: boolean;
  winnerBidId?: string | null;
  onPickWinner?: (bid: RussiaRequestCarrierBid) => void;
}

export function RussiaCarrierBidCard({
  carrier,
  canEdit,
  onAddBid,
  onEditCarrier,
  onDeleteCarrier,
  onEditBid,
  onDeleteBid,
  canPickWinner = false,
  winnerBidId = null,
  onPickWinner,
}: RussiaCarrierBidCardProps) {
  const { t } = useTranslation('russiaRequests');
  const isRejected = carrier.state === 'REJECTED';
  // INVARIANT (#1167): the new flow has no scope/override — the bid list
  //   is simply every bid on the join row.
  const bids = carrier.bids;
  const showAddCta = canEdit && !isRejected;
  const showCarrierKebab = canEdit && Boolean(onEditCarrier ?? onDeleteCarrier);
  const showBidKebab = canEdit && Boolean(onEditBid ?? onDeleteBid);
  // WHY (#1544): the winner pick is only offered on a carrier that has
  //   ANSWERED (its REQUEST-scope bid is the quote `success` consumes).
  const showWinnerPick = canPickWinner && carrier.state === 'ANSWERED' && Boolean(onPickWinner);
  const testidSuffix = carrier.id;

  return (
    <div
      data-testid={`russia-request-drawer-carrier-card-${testidSuffix}`}
      style={{
        background: 'var(--bg-1)',
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        padding: 12,
        display: 'grid',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span
          data-testid={`russia-request-drawer-carrier-display-code-${testidSuffix}`}
          style={{
            fontSize: 11,
            fontVariantNumeric: 'tabular-nums',
            padding: '2px 6px',
            borderRadius: 'var(--r-1)',
            background: 'var(--bg-2)',
            color: 'var(--fg-2)',
            fontWeight: 600,
          }}
        >
          {carrier.displayCode}
        </span>
        {carrier.nameSnapshot && (
          <span
            data-testid={`russia-request-drawer-carrier-name-${testidSuffix}`}
            style={{
              fontWeight: 500,
              fontSize: 13,
              overflowWrap: 'anywhere',
              minWidth: 0,
              color: 'var(--fg-1)',
            }}
          >
            {carrier.nameSnapshot}
          </span>
        )}
        <span style={{ marginLeft: 'auto' }}>
          <CarrierStatePill state={carrier.state} />
        </span>
        {showCarrierKebab && (
          <CarrierActionsMenu
            testidSuffix={testidSuffix}
            {...(onEditCarrier ? { onEdit: onEditCarrier } : {})}
            {...(onDeleteCarrier ? { onDelete: onDeleteCarrier } : {})}
          />
        )}
      </div>

      <div
        data-testid={`russia-request-drawer-carrier-added-at-${testidSuffix}`}
        style={{ fontSize: 11, color: 'var(--fg-3)' }}
      >
        {t('drawer.carriers.card.addedAt', { date: formatAddedAt(carrier.createdAt) })}
      </div>

      {carrier.contactsSnapshot && (
        <div
          data-testid={`russia-request-drawer-carrier-contacts-${testidSuffix}`}
          style={{
            fontSize: 12,
            color: 'var(--fg-2)',
            overflowWrap: 'anywhere',
            whiteSpace: 'pre-wrap',
          }}
        >
          <span
            style={{
              fontSize: 10,
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              color: 'var(--fg-3)',
              marginRight: 6,
            }}
          >
            {t('drawer.carriers.card.contactsHeading')}
          </span>
          {carrier.contactsSnapshot}
        </div>
      )}

      {isRejected && carrier.rejectReason && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--status-error-fg)',
            overflowWrap: 'anywhere',
            whiteSpace: 'pre-wrap',
          }}
        >
          {carrier.rejectReason}
        </div>
      )}

      {bids.length > 0 && (
        <ul
          data-testid={`russia-request-drawer-carrier-bids-${testidSuffix}`}
          style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}
        >
          {bids.map((b) => (
            <li key={b.id}>
              <BidRow
                bid={b}
                isWinner={winnerBidId === b.id}
                {...(showWinnerPick && onPickWinner ? { onPickWinner: () => onPickWinner(b) } : {})}
                {...(showBidKebab && onEditBid ? { onEdit: () => onEditBid(b) } : {})}
                {...(showBidKebab && onDeleteBid ? { onDelete: () => onDeleteBid(b) } : {})}
              />
            </li>
          ))}
        </ul>
      )}

      {showAddCta && (
        <div>
          <Button
            kind="primary"
            size="sm"
            icon="plus"
            data-testid={`russia-request-drawer-carrier-add-bid-${testidSuffix}`}
            onClick={onAddBid}
          >
            {t('drawer.carriers.card.addPrimary')}
          </Button>
        </div>
      )}
    </div>
  );
}

function formatPrice(price: number): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'decimal',
      maximumFractionDigits: 2,
    }).format(price);
  } catch {
    return String(price);
  }
}

function formatAddedAt(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

function daysLeftFromExpiresAt(expiresAt: string): number {
  const ms = new Date(expiresAt).getTime() - Date.now();
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

function BidRow({
  bid,
  isWinner,
  onPickWinner,
  onEdit,
  onDelete,
}: {
  bid: RussiaRequestCarrierBid;
  isWinner: boolean;
  onPickWinner?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const { t } = useTranslation('russiaRequests');
  const days = daysLeftFromExpiresAt(bid.expiresAt);
  return (
    <div
      data-testid={`russia-request-drawer-carrier-bid-${bid.id}`}
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 10,
        padding: '6px 8px',
        borderRadius: 'var(--r-1)',
        background: isWinner ? 'var(--status-success-bg)' : 'var(--bg-2)',
        fontSize: 13,
        flexWrap: 'wrap',
      }}
    >
      <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPrice(bid.price)}</strong>
      <span
        data-testid={`russia-request-drawer-carrier-bid-expiry-${bid.id}`}
        style={{
          color: bid.isExpired ? 'var(--status-error-fg)' : 'var(--fg-3)',
          fontSize: 12,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {bid.isExpired
          ? t('drawer.carriers.card.expired')
          : days <= 0
            ? t('drawer.carriers.card.expiresToday')
            : t('drawer.carriers.card.daysLeft', { days })}
      </span>
      {isWinner && (
        <span
          data-testid={`russia-request-drawer-carrier-bid-winner-badge-${bid.id}`}
          style={{
            fontSize: 10,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            fontWeight: 600,
            color: 'var(--status-success-fg)',
            padding: '1px 6px',
            borderRadius: 'var(--r-1)',
            border: '1px solid var(--status-success-fg)',
          }}
        >
          {t('drawer.carriers.winner.badge')}
        </span>
      )}
      {onPickWinner && !isWinner && (
        <Button
          kind="secondary"
          size="sm"
          icon="check-check"
          data-testid={`russia-request-drawer-carrier-bid-pick-winner-${bid.id}`}
          onClick={onPickWinner}
        >
          {t('drawer.carriers.winner.pick')}
        </Button>
      )}
      {(onEdit ?? onDelete) && (
        <span style={{ marginLeft: 'auto' }}>
          <BidActionsMenu
            testidSuffix={bid.id}
            {...(onEdit ? { onEdit } : {})}
            {...(onDelete ? { onDelete } : {})}
          />
        </span>
      )}
      {bid.comment && (
        <span
          style={{
            color: 'var(--fg-2)',
            fontSize: 12,
            overflowWrap: 'anywhere',
            whiteSpace: 'pre-wrap',
            flexBasis: '100%',
          }}
        >
          {bid.comment}
        </span>
      )}
    </div>
  );
}

function CarrierActionsMenu({
  testidSuffix,
  onEdit,
  onDelete,
}: {
  testidSuffix: string;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const { t } = useTranslation('russiaRequests');
  const ariaLabel = t('drawer.carriers.actions.menuAria');
  return (
    <Menu
      trigger={(props) => (
        <IconButton
          {...props}
          icon="more-vertical"
          label={ariaLabel}
          data-testid={`russia-request-drawer-carrier-kebab-${testidSuffix}`}
        />
      )}
    >
      {onEdit && <MenuItem onClick={onEdit}>{t('drawer.carriers.actions.editCarrier')}</MenuItem>}
      {onDelete && (
        <MenuItem onClick={onDelete}>{t('drawer.carriers.actions.deleteCarrier')}</MenuItem>
      )}
    </Menu>
  );
}

function BidActionsMenu({
  testidSuffix,
  onEdit,
  onDelete,
}: {
  testidSuffix: string;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const { t } = useTranslation('russiaRequests');
  const ariaLabel = t('drawer.carriers.actions.menuAria');
  return (
    <Menu
      trigger={(props) => (
        <IconButton
          {...props}
          icon="more-vertical"
          label={ariaLabel}
          data-testid={`russia-request-drawer-carrier-bid-kebab-${testidSuffix}`}
        />
      )}
    >
      {onEdit && <MenuItem onClick={onEdit}>{t('drawer.carriers.actions.editBid')}</MenuItem>}
      {onDelete && <MenuItem onClick={onDelete}>{t('drawer.carriers.actions.deleteBid')}</MenuItem>}
    </Menu>
  );
}
